/**
 * Metadata Service
 * 
 * Orchestrates all metadata adapters and provides a unified interface
 * for reading and writing metadata across different file formats.
 */

import { CanonicalMetadata, MetadataAdapter, MetadataValidationResult, validateCanonicalMetadata } from './canonical-schema';
import { ExifAdapter } from './adapters/exif-adapter';
import { AudioAdapter } from './adapters/audio-adapter';
import { VideoAdapter } from './adapters/video-adapter';

export class MetadataService {
  private adapters: MetadataAdapter[] = [];
  private exifAdapter: ExifAdapter;
  private audioAdapter: AudioAdapter;
  private videoAdapter: VideoAdapter;

  constructor() {
    this.exifAdapter = new ExifAdapter();
    this.audioAdapter = new AudioAdapter();
    this.videoAdapter = new VideoAdapter();
    
    this.adapters = [
      this.exifAdapter,
      this.audioAdapter,
      this.videoAdapter
    ];
  }

  /**
   * Read metadata from a file using the appropriate adapter
   */
  async readMetadata(file: File): Promise<{
    metadata: CanonicalMetadata;
    validation: MetadataValidationResult;
    adapter: string;
  }> {
    const adapter = this.getAdapterForFile(file);
    if (!adapter) {
      throw new Error(`No adapter found for file type: ${file.type}`);
    }

    try {
      const metadata = await adapter.read(file);
      const validation = validateCanonicalMetadata(metadata);
      
      return {
        metadata,
        validation,
        adapter: adapter.constructor.name
      };
    } catch (error) {
      console.error('Metadata read error:', error);
      throw new Error(`Failed to read metadata from ${file.name}: ${error}`);
    }
  }

  /**
   * Write metadata to a file using the appropriate adapter
   */
  async writeMetadata(file: File, metadata: CanonicalMetadata): Promise<File> {
    const adapter = this.getAdapterForFile(file);
    if (!adapter) {
      throw new Error(`No adapter found for file type: ${file.type}`);
    }

    // Validate metadata before writing
    const validation = validateCanonicalMetadata(metadata);
    if (!validation.isValid) {
      throw new Error(`Metadata validation failed: ${validation.errors.map(e => e.message).join(', ')}`);
    }

    try {
      return await adapter.write(file, metadata);
    } catch (error) {
      console.error('Metadata write error:', error);
      throw new Error(`Failed to write metadata to ${file.name}: ${error}`);
    }
  }

  /**
   * Get the appropriate adapter for a file
   */
  private getAdapterForFile(file: File): MetadataAdapter | null {
    return this.adapters.find(adapter => adapter.supports(file)) || null;
  }

  /**
   * Get all supported file extensions
   */
  getSupportedExtensions(): string[] {
    const extensions = new Set<string>();
    this.adapters.forEach(adapter => {
      adapter.getSupportedExtensions().forEach(ext => extensions.add(ext));
    });
    return Array.from(extensions).sort();
  }

  /**
   * Check if a file type is supported
   */
  isSupported(file: File): boolean {
    return this.getAdapterForFile(file) !== null;
  }

  /**
   * Get file type category
   */
  getFileCategory(file: File): 'image' | 'audio' | 'video' | 'document' | 'unknown' {
    if (file.type.startsWith('image/')) return 'image';
    if (file.type.startsWith('audio/')) return 'audio';
    if (file.type.startsWith('video/')) return 'video';
    if (file.type.includes('document') || file.type.includes('pdf') || file.type.includes('text')) return 'document';
    return 'unknown';
  }

  /**
   * Merge metadata from multiple sources with provenance tracking
   */
  mergeMetadata(sources: Array<{ metadata: CanonicalMetadata; source: string; confidence: number }>): CanonicalMetadata {
    const merged: CanonicalMetadata = {};
    const provenance: Array<{ field: string; source: string; confidence: number }> = [];

    // Sort sources by confidence (highest first)
    const sortedSources = sources.sort((a, b) => b.confidence - a.confidence);

    sortedSources.forEach(({ metadata, source, confidence }) => {
      Object.entries(metadata).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          // Only set if not already set or if this source has higher confidence
          const existingProvenance = provenance.find(p => p.field === key);
          if (!existingProvenance || confidence > existingProvenance.confidence) {
            (merged as any)[key] = value;
            if (existingProvenance) {
              existingProvenance.source = source;
              existingProvenance.confidence = confidence;
            } else {
              provenance.push({ field: key, source, confidence });
            }
          }
        }
      });
    });

    return merged;
  }

  /**
   * Convert metadata to different formats for export
   */
  exportMetadata(metadata: CanonicalMetadata, format: 'json' | 'csv' | 'xml' | 'yaml'): string {
    switch (format) {
      case 'json':
        return JSON.stringify(metadata, null, 2);
      
      case 'csv':
        return this.exportToCSV(metadata);
      
      case 'xml':
        return this.exportToXML(metadata);
      
      case 'yaml':
        return this.exportToYAML(metadata);
      
      default:
        throw new Error(`Unsupported export format: ${format}`);
    }
  }

  /**
   * Import metadata from different formats
   */
  importMetadata(content: string, format: 'json' | 'csv' | 'xml' | 'yaml'): CanonicalMetadata {
    switch (format) {
      case 'json':
        return JSON.parse(content);
      
      case 'csv':
        return this.importFromCSV(content);
      
      case 'xml':
        return this.importFromXML(content);
      
      case 'yaml':
        return this.importFromYAML(content);
      
      default:
        throw new Error(`Unsupported import format: ${format}`);
    }
  }

  private exportToCSV(metadata: CanonicalMetadata): string {
    const rows: string[] = ['Field,Value,Category'];
    
    Object.entries(metadata).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        let displayValue = '';
        if (typeof value === 'object') {
          displayValue = JSON.stringify(value);
        } else if (Array.isArray(value)) {
          displayValue = value.join(', ');
        } else {
          displayValue = String(value);
        }
        rows.push(`"${key}","${displayValue}","${this.getFieldCategory(key)}"`);
      }
    });
    
    return rows.join('\n');
  }

  private exportToXML(metadata: CanonicalMetadata): string {
    let xml = '<?xml version="1.0" encoding="UTF-8"?>\n<metadata>\n';
    
    Object.entries(metadata).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        if (typeof value === 'object' && !Array.isArray(value)) {
          xml += `  <${key}>\n`;
          Object.entries(value as Record<string, any>).forEach(([subKey, subValue]) => {
            if (subValue !== undefined && subValue !== null) {
              xml += `    <${subKey}>${this.escapeXML(String(subValue))}</${subKey}>\n`;
            }
          });
          xml += `  </${key}>\n`;
        } else if (Array.isArray(value)) {
          xml += `  <${key}>\n`;
          value.forEach(item => {
            xml += `    <item>${this.escapeXML(String(item))}</item>\n`;
          });
          xml += `  </${key}>\n`;
        } else {
          xml += `  <${key}>${this.escapeXML(String(value))}</${key}>\n`;
        }
      }
    });
    
    xml += '</metadata>';
    return xml;
  }

  private exportToYAML(metadata: CanonicalMetadata): string {
    // Simple YAML export (in a real implementation, use a proper YAML library)
    let yaml = '';
    
    Object.entries(metadata).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        if (typeof value === 'object' && !Array.isArray(value)) {
          yaml += `${key}:\n`;
          Object.entries(value as Record<string, any>).forEach(([subKey, subValue]) => {
            if (subValue !== undefined && subValue !== null) {
              yaml += `  ${subKey}: ${subValue}\n`;
            }
          });
        } else if (Array.isArray(value)) {
          yaml += `${key}:\n`;
          value.forEach(item => {
            yaml += `  - ${item}\n`;
          });
        } else {
          yaml += `${key}: ${value}\n`;
        }
      }
    });
    
    return yaml;
  }

  private importFromCSV(content: string): CanonicalMetadata {
    const lines = content.split('\n');
    const metadata: CanonicalMetadata = {};
    
    // Skip header
    for (let i = 1; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) continue;
      
      const [field, value] = line.split(',').map(s => s.replace(/^"|"$/g, ''));
      if (field && value) {
        (metadata as any)[field] = value;
      }
    }
    
    return metadata;
  }

  private importFromXML(content: string): CanonicalMetadata {
    // Simple XML parsing (in a real implementation, use a proper XML parser)
    const metadata: CanonicalMetadata = {};
    const parser = new DOMParser();
    const doc = parser.parseFromString(content, 'text/xml');
    
    const elements = doc.querySelectorAll('metadata > *');
    elements.forEach(element => {
      const key = element.tagName;
      const value = element.textContent;
      if (value) {
        (metadata as any)[key] = value;
      }
    });
    
    return metadata;
  }

  private importFromYAML(content: string): CanonicalMetadata {
    // Simple YAML parsing (in a real implementation, use a proper YAML library)
    const metadata: CanonicalMetadata = {};
    const lines = content.split('\n');
    
    lines.forEach(line => {
      const match = line.match(/^(\w+):\s*(.+)$/);
      if (match) {
        const [, key, value] = match;
        (metadata as any)[key] = value;
      }
    });
    
    return metadata;
  }

  private getFieldCategory(field: string): string {
    const categories: Record<string, string> = {
      title: 'basic',
      description: 'basic',
      keywords: 'basic',
      creator: 'basic',
      copyright: 'basic',
      gps: 'geographic',
      rating: 'basic',
      colorLabel: 'basic',
      people: 'basic',
      album: 'media',
      show: 'media',
      artwork: 'media',
      technical: 'technical',
      provenance: 'workflow',
      language: 'basic',
      locale: 'basic',
      custom: 'custom'
    };
    
    return categories[field] || 'unknown';
  }

  private escapeXML(str: string): string {
    return str
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }
}

// Singleton instance
export const metadataService = new MetadataService();
